'use client';

import { c as _c } from "react/compiler-runtime";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { usePathname } from 'next/navigation';
import { SystemNotification } from '@/shared/schema/system-notification';
import { notificationApi } from '../api/notification-api';
import { useIdentity } from '@/lib/providers/identity';
export interface SystemNotificationState {
  notifications: SystemNotification[];
  dismissNotification: (id: string) => void;
  refreshNotifications: () => void;
  isLoading: boolean;
  error: Error | null;
}
export function useSystemNotifications() {
  const $ = _c(33);
  const {
    identity
  } = useIdentity();
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const currentPage = pathname || "/";
  const userId = identity?.userId;
  const organizationId = identity?.orgId ?? undefined;
  let t0;
  if ($[0] !== currentPage || $[1] !== organizationId || $[2] !== userId) {
    t0 = ["notifications", userId, organizationId, currentPage];
    $[0] = currentPage;
    $[1] = organizationId;
    $[2] = userId;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  let t1;
  if ($[4] !== currentPage || $[5] !== organizationId) {
    t1 = () => notificationApi.getNotifications({
      page: currentPage,
      orgId: organizationId
    });
    $[4] = currentPage;
    $[5] = organizationId;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = !!userId;
  let t3;
  if ($[7] !== t0 || $[8] !== t1 || $[9] !== t2) {
    t3 = {
      queryKey: t0,
      queryFn: t1,
      enabled: t2,
      staleTime: 30000,
      refetchInterval: 60000
    };
    $[7] = t0;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const {
    data: t4,
    error,
    isLoading,
    refetch: refreshNotifications
  } = useQuery(t3);
  let t5;
  if ($[11] !== t4) {
    t5 = t4 === undefined ? [] : t4;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const notifications = t5;
  let t6;
  if ($[13] !== organizationId) {
    t6 = notificationId => notificationApi.dismissNotification({
      notificationId,
      organizationId
    });
    $[13] = organizationId;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  let t8;
  let t9;
  if ($[15] !== currentPage || $[16] !== organizationId || $[17] !== queryClient || $[18] !== userId) {
    t7 = async notificationId_0 => {
      await queryClient.cancelQueries({
        queryKey: ["notifications", userId, organizationId, currentPage]
      });
      const previousNotifications = queryClient.getQueryData(["notifications", userId, organizationId, currentPage]);
      queryClient.setQueryData(["notifications", userId, organizationId, currentPage], t10 => {
        const old = t10 === undefined ? [] : t10;
        return old.filter(n => n.id !== notificationId_0);
      });
      return {
        previousNotifications
      };
    };
    t8 = (err, notificationId_1, context) => {
      if (context?.previousNotifications) {
        queryClient.setQueryData(["notifications", userId, organizationId, currentPage], context.previousNotifications);
      }
    };
    t9 = () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", userId, organizationId, currentPage]
      });
    };
    $[15] = currentPage;
    $[16] = organizationId;
    $[17] = queryClient;
    $[18] = userId;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t6 || $[23] !== t7 || $[24] !== t8 || $[25] !== t9) {
    t10 = {
      mutationFn: t6,
      onMutate: t7,
      onError: t8,
      onSettled: t9
    };
    $[22] = t6;
    $[23] = t7;
    $[24] = t8;
    $[25] = t9;
    $[26] = t10;
  } else {
    t10 = $[26];
  }
  const dismissMutation = useMutation(t10);
  let t11;
  if ($[27] !== dismissMutation.mutate || $[28] !== error || $[29] !== isLoading || $[30] !== notifications || $[31] !== refreshNotifications) {
    t11 = {
      notifications,
      dismissNotification: dismissMutation.mutate,
      refreshNotifications,
      isLoading,
      error
    };
    $[27] = dismissMutation.mutate;
    $[28] = error;
    $[29] = isLoading;
    $[30] = notifications;
    $[31] = refreshNotifications;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  return t11;
}